<template>
  <div class="page-content content" v-if="live">
    <div class="roster-header position-relative text-center mb-3">
      <h1 class="display-4 mb-0">{{ $t('live_roster.title') }}</h1>
      <button class="btn btn-success roster-header__back" @click="backToLive()">
        <i class="fa fa-arrow-right mr-1"></i>{{ $t('live_roster.back_to_live') }}
      </button>
    </div>
    <div class="row">
      <div v-for="teamKey in teamKeys" :key="teamKey" class="col-xl-5 col-md-6" :class="{ 'offset-xl-1': teamKey === 'homeTeam' }">
        <button v-if="config.pre_game.add_players" class="btn btn-sm btn-success w-100" @click="playerCreateSidebar(live[teamKey].uuid)">{{ $t('player.add') }}</button>
        <ntm-block :title="live[teamKey].name">
          <table v-if="rowsByTeam[teamKey].active.length + rowsByTeam[teamKey].bench.length > 0" class="table table-hover table-vcenter font-size-sm">
            <thead>
            <tr>
              <th class="text-center">{{ $t('live_roster.jersey') }}</th>
              <th>{{ $t('pre_game.table.name') }}</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="row in rowsByTeam[teamKey].active" :key="row.player.uuid">
              <td class="text-center number-of-jersey-column">
                <input
                  :ref="'jersey-' + row.player.uuid"
                  class="form-control number-of-jersey-input"
                  type="number"
                  min="0"
                  :placeholder="row.registered ? '' : (row.player.number_of_jersey_suggestion || '')"
                  v-model="jerseyDrafts[row.player.uuid]"
                  @blur="saveJersey(teamKey, row)"
                  @keyup.enter="$event.target.blur()">
              </td>
              <td class="font-w600">
                <!-- the name with the birth year always below it, centered in the row -->
                <div class="d-flex flex-column justify-content-center">
                  <span>{{ row.player.name }}</span>
                  <small v-if="row.player.birthYear" class="text-muted font-w400">{{ row.player.birthYear }}</small>
                </div>
              </td>
              <td class="text-right">
                <button v-if="row.registered" class="btn btn-sm btn-secondary" v-tooltip="$t('live_roster.swap_player')" @click="openChangePlayer(teamKey, row.player)">
                  <i class="fa fa-exchange"></i><span class="d-none d-sm-inline ml-1">{{ $t('live_roster.swap_player') }}</span>
                </button>
                <button v-if="!row.registered" class="btn btn-sm btn-light" @mousedown.prevent @click="cancelRegister(row.player)">
                  <i class="fa fa-times"></i>
                </button>
                <button v-if="row.registered" class="btn btn-sm btn-danger ml-1" :disabled="!removableUuids.includes(row.player.uuid)" v-tooltip="removableUuids.includes(row.player.uuid) ? $t('live_roster.remove_player') : $t('live_roster.player_has_activity')" @click="removePlayer(row.player)">
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
            <tr v-if="rowsByTeam[teamKey].bench.length > 0" class="roster-divider">
              <td colspan="3">{{ $t('live_roster.not_in_game') }}</td>
            </tr>
            <tr v-for="row in rowsByTeam[teamKey].bench" :key="'bench-' + row.player.uuid">
              <td class="text-center number-of-jersey-column">
                <button class="btn btn-sm btn-success" v-tooltip="$t('live_roster.add_to_game')" @click="startRegister(row.player)">
                  <i class="fa fa-plus"></i>
                </button>
              </td>
              <td class="font-w600 text-muted">
                <div class="d-flex flex-column justify-content-center">
                  <span>{{ row.player.name }}</span>
                  <small v-if="row.player.birthYear" class="font-w400">{{ row.player.birthYear }}</small>
                </div>
              </td>
              <td></td>
            </tr>
            </tbody>
          </table>
          <p v-else class="text-muted text-center">{{ $t('live_roster.no_players') }}</p>
        </ntm-block>
        <!-- While the list is open the block gets room below it, so the whole list is visible at the bottom of the page -->
        <div class="mb-4" :class="{ 'roster-add-player--open': addOpen[teamKey] }">
          <label class="mb-1 font-w600" :for="'roster-add-player-' + teamKey">{{ $t('player.addExisting') }}</label>
          <v-select
            :inputId="'roster-add-player-' + teamKey"
            label="name"
            :options="addOptions(teamKey)"
            :filter="(options) => options"
            :placeholder="$t('live_roster.select_player')"
            v-model="addForms[teamKey].selected"
            @search="(search, loading) => searchPlayer(search, loading, teamKey)"
            @open="openAddList(teamKey)"
            @close="addOpen[teamKey] = false">
            <template #option="option">
              {{ option.name }}<small v-if="option.birthYear" class="ml-1" style="opacity: .7">({{ option.birthYear }})</small>
            </template>
            <template #selected-option="option">
              {{ option.name }}<small v-if="option.birthYear" class="ml-1" style="opacity: .7">({{ option.birthYear }})</small>
            </template>
            <template #no-options>
              <p class="info">{{ $tc('player.searchAndAdd') }}</p>
            </template>
          </v-select>
          <div v-if="addForms[teamKey].selected" class="d-flex mt-2">
            <input
              class="form-control mr-2"
              type="number"
              min="0"
              :placeholder="$t('live_roster.jersey')"
              v-model="addForms[teamKey].jersey"
              @keyup.enter="submitAdd(teamKey)">
            <button class="btn btn-success flex-shrink-0" :disabled="!canSubmitAdd(teamKey) || addForms[teamKey].loading" @click="submitAdd(teamKey)">
              <i v-if="addForms[teamKey].loading" class="fa fa-cog fa-spin"></i>
              <span v-else>{{ $t('save') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <modal name="live-roster-player" height="auto" :minWidth="280" width="420" :scrollable="true">
      <div class="p-4">
        <h6 class="mb-3">
          {{ $t('live_roster.swap_player') }}
          <span v-if="modal.player"> - {{ modal.player.name }}</span>
        </h6>
        <div class="swap-options">
          <player-card
            v-for="player in swapOptions"
            :key="player.uuid"
            :player="player"
            :disabled="modal.loading"
            :show-suggestion="true"
            class="mb-2"
            @click="swapPlayer(player)"
          >
            <template #aside>
              <small v-if="player.inGame" class="text-muted ml-2 flex-shrink-0">{{ $t('live_roster.in_the_game') }}</small>
            </template>
          </player-card>
          <p v-if="swapOptions.length === 0" class="text-muted text-center mb-0">{{ $t('live_roster.no_players') }}</p>
        </div>
      </div>
    </modal>

    <ntm-sidebar @close="teamUuid = null" :title="$t('player.create')" name="add-player">
      <form id="playerForm" @submit.prevent="onPlayerSubmit()">
        <formly-form :form="playerForm" :model="playerModel" :fields="playerFields"></formly-form>
        <button class="btn btn-success push-15" :disabled="playerSaving">{{ $t('save') }}</button>
      </form>
    </ntm-sidebar>
  </div>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'
import router from '@/router'
import store from '@/store'
import GameService from '@/services/game.service'
import TeamService from '@/services/team.service'
import GlobalService from '@/services/global.service'
import PlayerCard from '@/components/le/PlayerCard.vue'
import playerFields from '@/formDefinitions/player'
import { useRouteQuery } from '@/use/useRouteQuery'
import { GameToastMessage } from '@/lib/toast'

export default {
  name: 'liveRoster',
  components: { PlayerCard },
  setup () {
    // The sidebar with the form of a new player is opened through the route query, like on the pre game screen
    const addPlayer = useRouteQuery('add-player')
    const teamUuid = useRouteQuery('team')
    return { addPlayer, teamUuid }
  },
  data () {
    return {
      playerFields,
      playerForm: {},
      playerSaving: false,
      playerModel: { firstName: '', lastName: '', middleName: '', birthYear: '' },
      live: null,
      roster: null,
      removableUuids: [],
      teamKeys: ['homeTeam', 'awayTeam'],
      jerseyDrafts: {},
      pending: {},
      // Players found through the global search, per team (the "Add player" search under each team)
      searchedPlayers: { homeTeam: [], awayTeam: [] },
      addForms: {
        homeTeam: { selected: null, jersey: '', loading: false },
        awayTeam: { selected: null, jersey: '', loading: false }
      },
      addOpen: { homeTeam: false, awayTeam: false },
      // The swap player modal
      modal: {
        teamKey: null,
        player: null,
        loading: false
      }
    }
  },
  beforeRouteEnter (to, from, next) {
    if (!store.getters.isAdminOrInternalScorekeeper && !store.getters.isExternalScorekeeper) {
      return next('/')
    }
    GameService.status().then((response) => {
      if (response.data === 'SCHEDULED') {
        return next({ name: 'preGame', params: to.params })
      }
      if (response.data === 'FINISHED') {
        return next({ name: 'archive', params: to.params })
      }
      Promise.all([GameService.live(), GameService.preGame(), GameService.removableLivePlayers()]).then(([liveResponse, preGameResponse, removableResponse]) => {
        next(vm => vm.setData(liveResponse.data, preGameResponse.data, removableResponse.data.data))
      })
    })
  },
  computed: {
    config () {
      return this.$store.state.config
    },
    // The whole team roster is always listed. Players with a jersey number (or just picked with "+" and
    // waiting for a number) are above the line, everybody else is below it.
    rowsByTeam () {
      const rows = {}
      this.teamKeys.forEach(teamKey => {
        const inGame = this.live[teamKey].players
        const inGameUuids = inGame.map(player => player.uuid)
        const others = this.roster[teamKey].players.filter(player => !inGameUuids.includes(player.uuid))
        rows[teamKey] = {
          active: [
            ...inGame.map(player => ({ player, registered: true })),
            ...others.filter(player => this.pending[player.uuid]).map(player => ({ player, registered: false }))
          ],
          bench: others.filter(player => !this.pending[player.uuid]).map(player => ({ player, registered: false }))
        }
      })
      return rows
    },
    liveUuids () {
      return this.teamKeys.flatMap(teamKey => this.live[teamKey].players.map(player => player.uuid))
    },
    // Everybody of the team except the player being swapped out: players not in the game first (selectable)
    swapOptions () {
      if (!this.modal.teamKey) return []
      const options = this.roster[this.modal.teamKey].players
        .filter(player => player.uuid !== this.modal.player?.uuid)
        .map(player => ({ ...player, inGame: this.liveUuids.includes(player.uuid) }))

      return [...options.filter(player => !player.inGame), ...options.filter(player => player.inGame)]
    }
  },
  methods: {
    setData (live, roster, removableUuids) {
      this.removableUuids = removableUuids
      this.live = live
      this.roster = roster
      const drafts = {}
      const pending = {}
      this.teamKeys.forEach(teamKey => {
        live[teamKey].players.forEach(player => {
          drafts[player.uuid] = player.number_of_jersey
        })
      })
      // Players picked with "+" and still waiting for a number stay as they are
      Object.keys(this.pending).forEach(uuid => {
        if (!(uuid in drafts)) {
          pending[uuid] = true
          drafts[uuid] = this.jerseyDrafts[uuid] ?? ''
        }
      })
      this.jerseyDrafts = drafts
      this.pending = pending
    },
    // "+" on a player below the line: move him up and wait for his jersey number
    startRegister (player) {
      this.$set(this.jerseyDrafts, player.uuid, '')
      this.$set(this.pending, player.uuid, true)
      this.$nextTick(() => {
        const input = this.$refs['jersey-' + player.uuid]
        const element = Array.isArray(input) ? input[0] : input
        if (element) element.focus()
      })
    },
    reload () {
      return Promise.all([GameService.live(), GameService.preGame(), GameService.removableLivePlayers()]).then(([liveResponse, preGameResponse, removableResponse]) => {
        this.setData(liveResponse.data, preGameResponse.data, removableResponse.data.data)
      })
    },
    showError (err) {
      const errors = err.response?.data?.errors
      const message = errors ? Object.values(errors).flat()[0] : (err.message || 'Error')
      GameToastMessage(this.translateError(message), 'error')
    },
    // The API sends translation keys as "key" or "key|param" (e.g. "player.in_another_team|Team name")
    translateError (message) {
      const [key, param] = String(message).split('|')
      if (!this.$te(key)) return message
      return param ? `${this.$t(key)} (${param})` : this.$t(key)
    },
    saveJersey (teamKey, { player, registered }) {
      const original = registered ? player.number_of_jersey : ''
      const value = this.jerseyDrafts[player.uuid]
      if (value === '' || value === null || String(value) === String(original)) {
        // Nothing to save. A player picked with "+" stays above the line waiting for his number
        // (without a number he is simply not saved), so several players can be picked first.
        this.$set(this.jerseyDrafts, player.uuid, original)
        return
      }
      const request = registered
        ? GameService.updateLivePlayer(player.uuid, { numberOfJersey: value })
        : GameService.addLivePlayer({ teamUuid: this.live[teamKey].uuid, playerUuid: player.uuid, numberOfJersey: value })

      // The response is the whole live game: only this row is changed, the other rows (and the numbers
      // being typed into them) stay as they are
      request.then((response) => {
        GameToastMessage(this.$t('live_roster.updated'))
        this.live = response.data
        this.$set(this.jerseyDrafts, player.uuid, value)
        if (!registered) {
          this.$delete(this.pending, player.uuid)
          this.removableUuids = [...this.removableUuids, player.uuid]
        }
      }).catch((err) => {
        this.showError(err)
        this.$set(this.jerseyDrafts, player.uuid, original)
      })
    },
    // Takes a player picked with "+" back below the line
    cancelRegister (player) {
      this.$delete(this.pending, player.uuid)
      this.$delete(this.jerseyDrafts, player.uuid)
    },
    openChangePlayer (teamKey, player) {
      this.modal = { teamKey, player, loading: false }
      this.$modal.show('live-roster-player')
    },
    // Options of the "Add player" search of a team: the players of the team who are not in the game, then the
    // players found through the global search (who might not be in the team yet)
    addOptions (teamKey) {
      const rosterPlayers = this.roster[teamKey].players
      const options = rosterPlayers.concat(this.searchedPlayers[teamKey].filter(found => !rosterPlayers.some(p => p.uuid === found.uuid)))
      return options.filter(player => !this.liveUuids.includes(player.uuid))
    },
    // Opens the sidebar with the form of a new player of the team (same as "Add new player" on the pre game screen)
    playerCreateSidebar (teamUuid) {
      if (!this.config.pre_game.add_players) return
      this.addPlayer = true
      this.teamUuid = teamUuid
      this.playerForm.$errors = {}
      this.playerModel = { firstName: '', lastName: '', middleName: '', birthYear: '' }
      setTimeout(() => {
        const firstField = document.querySelector('#playerForm .form-control')
        if (firstField) firstField.focus()
      })
    },
    // Creates the player in the system, adds him to the team and shows him in the list of the team (not in the game yet)
    onPlayerSubmit () {
      this.playerForm.$errors = {}
      this.playerSaving = true
      TeamService.createAndAddNewPlayerToTeam(this.teamUuid, this.playerModel).then(async () => {
        this.addPlayer = null
        this.teamUuid = null
        this.playerModel = { firstName: '', lastName: '', middleName: '', birthYear: '' }
        GameToastMessage(this.$t('live_roster.updated'))
        await this.reload()
      }).catch((err) => {
        this.playerForm.$errors = err.response?.data?.errors || {}
      }).finally(() => {
        this.playerSaving = false
      })
    },
    openAddList (teamKey) {
      this.addOpen[teamKey] = true
      // the block grows first, then the search is scrolled so that the list below it is visible
      this.$nextTick(() => {
        const input = document.getElementById('roster-add-player-' + teamKey)
        if (input) input.scrollIntoView({ block: 'center' })
      })
    },
    canSubmitAdd (teamKey) {
      const form = this.addForms[teamKey]
      return Boolean(form.selected) && form.jersey !== '' && form.jersey !== null
    },
    searchPlayer (search, loading, teamKey) {
      if (search.length === 0) {
        this.searchedPlayers[teamKey] = []
        return
      }
      loading(true)
      this.searchGlobalPlayers(search, loading, teamKey)
    },
    searchGlobalPlayers: _.debounce(function (search, loading, teamKey) {
      GlobalService.searchPlayers(search).then((res) => {
        this.searchedPlayers[teamKey] = res.data.filter(player => player.uuid !== 'add')
      }).finally(() => loading(false))
    }, 300),
    // One click on a player of the team swaps him in for the player of the modal (jersey number stays on the row).
    // When the clicked player is already in the game, the two players trade their stats and logs.
    async swapPlayer (player) {
      if (this.modal.loading) return
      this.modal.loading = true
      try {
        await GameService.updateLivePlayer(this.modal.player.uuid, { newPlayerUuid: player.uuid })
        this.$modal.hide('live-roster-player')
        GameToastMessage(this.$t('live_roster.updated'))
        await this.reload()
      } catch (err) {
        this.showError(err)
      } finally {
        this.modal.loading = false
      }
    },
    // Possible only for a player without logs and substitutions; the player goes back to "Not in the game"
    async removePlayer (player) {
      try {
        await GameService.removeLivePlayer(player.uuid)
        GameToastMessage(this.$t('live_roster.removed'))
        await this.reload()
      } catch (err) {
        if (axios.isCancel(err)) return
        this.showError(err)
      }
    },
    // Adds the player picked in the "Add player" search of the team to the game with the typed jersey number
    async submitAdd (teamKey) {
      const form = this.addForms[teamKey]
      if (!this.canSubmitAdd(teamKey) || form.loading) return
      form.loading = true
      try {
        const teamUuid = this.live[teamKey].uuid
        const selected = form.selected
        // A player found through the global search might not be in the team yet
        const inTeam = this.roster[teamKey].players.some(player => player.uuid === selected.uuid)
        if (!inTeam) {
          await TeamService.addPlayerToTeam(teamUuid, { playerUuid: selected.uuid })
        }
        await GameService.addLivePlayer({ teamUuid, playerUuid: selected.uuid, numberOfJersey: form.jersey })
        form.selected = null
        form.jersey = ''
        this.searchedPlayers[teamKey] = []
        GameToastMessage(this.$t('live_roster.updated'))
        await this.reload()
      } catch (err) {
        this.showError(err)
      } finally {
        form.loading = false
      }
    },
    backToLive () {
      router.push({ name: 'live' })
    }
  }
}
</script>

<style scoped>
.roster-add-player--open {
  /* the height of the open list of the search (vue-select menu) */
  padding-bottom: 22rem;
}

.swap-options {
  max-height: 60vh;
  overflow-y: auto;
}

.number-of-jersey-input::placeholder {
  color: #c5cbd3;
  font-style: italic;
  opacity: 1;
}

.roster-divider td {
  padding: 0.4rem 0.75rem;
  border-top: 2px solid #c9cfd8;
  background: #f6f7f9;
  color: #8a94a3;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.roster-header__back {
  margin-top: 0.75rem;
}

@media (min-width: 768px) {
  .roster-header__back {
    position: absolute;
    top: 50%;
    right: 0;
    margin-top: 0;
    transform: translateY(-50%);
  }
}
</style>
